import React, { useState, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Database, Tag, Monitor, MousePointer, Shield, Clock,
  ChevronDown, ChevronRight, Copy, Check, Info, Code
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';

const categoryIcons = {
  attribution: Tag,
  technical: Monitor,
  behavior: MousePointer,
  quality: Shield,
  context: Clock
};

const categoryColors = {
  attribution: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  technical: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  behavior: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  quality: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  context: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' }
};

const AdminTracking = () => {
  const { settings } = useSettings();
  const [trackingFields, setTrackingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const [showCode, setShowCode] = useState(false);
  
  const primaryColor = settings?.primaryColor || '#00b30c';

  useEffect(() => {
    loadTrackingSettings();
  }, []);

  const loadTrackingSettings = async () => {
    try {
      const data = await api.getTrackingSettings();
      setTrackingFields(data.trackingFields || {});
      // Expand all categories by default
      const expanded = {};
      Object.keys(data.trackingFields || {}).forEach(key => {
        expanded[key] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error loading tracking settings:', error);
      toast.error('Error al cargar configuración de tracking');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(text);
    setTimeout(() => setCopiedKey(null), 2000);
    toast.success('Copiado al portapapeles');
  };

  const getTotalFields = () => {
    let total = 0;
    Object.values(trackingFields).forEach(cat => {
      total += cat.fields?.length || 0;
    });
    return total;
  };

  // Generate JavaScript code for tracking - COMPLETE VERSION
  const trackingCode = `
// DataCRM Tracking Script v2.0
// Instrucciones: Copia este código antes del cierre de </body>
(function() {
  'use strict';
  
  // Variables de estado
  var pageLoadTime = Date.now();
  var maxScrollDepth = 0;
  var formStartTime = null;
  var emailPasted = false;
  var autofillDetected = false;
  
  // Obtener parámetros UTM y click IDs de la URL
  function getUrlParams() {
    var params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      fbclid: params.get('fbclid') || ''
    };
  }
  
  // Almacenar primera URL de entrada
  if (!sessionStorage.getItem('datacrm_first_url')) {
    sessionStorage.setItem('datacrm_first_url', window.location.href);
    // Guardar UTMs de la primera entrada
    var utmParams = getUrlParams();
    sessionStorage.setItem('datacrm_utms', JSON.stringify(utmParams));
  }
  
  // Contador de visitas
  var visitCount = parseInt(localStorage.getItem('datacrm_visits') || '0') + 1;
  localStorage.setItem('datacrm_visits', visitCount);
  
  // Detectar tipo de dispositivo
  function getDeviceType() {
    var width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }
  
  // Rastrear scroll
  function trackScroll() {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var docHeight = document.documentElement.scrollHeight - window.innerHeight;
    var scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
    if (scrollPercent > maxScrollDepth) {
      maxScrollDepth = scrollPercent;
    }
  }
  window.addEventListener('scroll', trackScroll, { passive: true });
  
  // Detectar inicio de interacción con formulario
  document.addEventListener('focusin', function(e) {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
      if (!formStartTime) {
        formStartTime = Date.now();
      }
    }
  });
  
  // Detectar pegado de email
  document.addEventListener('paste', function(e) {
    var target = e.target;
    if (target.type === 'email' || target.name === 'email' || target.id === 'email') {
      emailPasted = true;
    }
  });
  
  // Detectar autofill del navegador
  setTimeout(function() {
    var inputs = document.querySelectorAll('input');
    inputs.forEach(function(input) {
      try {
        if (input.matches(':-webkit-autofill')) {
          autofillDetected = true;
        }
      } catch(e) {}
    });
  }, 1000);
  
  // Función principal para obtener todos los datos de tracking
  function getTrackingData() {
    var storedUtms = {};
    try {
      storedUtms = JSON.parse(sessionStorage.getItem('datacrm_utms') || '{}');
    } catch(e) {}
    
    var currentUtms = getUrlParams();
    // Priorizar UTMs actuales sobre los almacenados
    var utms = {
      utm_source: currentUtms.utm_source || storedUtms.utm_source || '',
      utm_medium: currentUtms.utm_medium || storedUtms.utm_medium || '',
      utm_campaign: currentUtms.utm_campaign || storedUtms.utm_campaign || '',
      utm_term: currentUtms.utm_term || storedUtms.utm_term || '',
      utm_content: currentUtms.utm_content || storedUtms.utm_content || '',
      gclid: currentUtms.gclid || storedUtms.gclid || '',
      fbclid: currentUtms.fbclid || storedUtms.fbclid || ''
    };
    
    var timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
    var formFillDuration = formStartTime ? Math.round((Date.now() - formStartTime) / 1000) : 0;
    
    return {
      // Atribución (Marketing)
      utm_source: utms.utm_source,
      utm_medium: utms.utm_medium,
      utm_campaign: utms.utm_campaign,
      utm_term: utms.utm_term,
      utm_content: utms.utm_content,
      gclid: utms.gclid,
      fbclid: utms.fbclid,
      referrer_url: document.referrer || '',
      
      // Datos Técnicos
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
      browser_language: navigator.language || navigator.userLanguage || '',
      screen_resolution: screen.width + 'x' + screen.height,
      connection_type: (navigator.connection && navigator.connection.effectiveType) || 'unknown',
      
      // Comportamiento
      first_touch_url: sessionStorage.getItem('datacrm_first_url') || window.location.href,
      conversion_url: window.location.href,
      visit_count: visitCount,
      scroll_depth_pct: maxScrollDepth,
      saw_whole_page: maxScrollDepth >= 90,
      time_on_page_sec: timeOnPage,
      
      // Calidad del Formulario
      form_fill_duration: formFillDuration,
      email_pasted: emailPasted,
      used_autofill: autofillDetected,
      is_bot_honeypot: false, // Se actualiza con honeypot
      
      // Contexto
      user_local_time: new Date().toISOString()
    };
  }
  
  // Función para verificar honeypot (campo oculto)
  function checkHoneypot(formElement) {
    var honeypotSelectors = [
      'input[name="website"]',
      'input[name="url"]', 
      'input[name="fax"]',
      'input.honeypot',
      'input[data-honeypot]'
    ];
    
    for (var i = 0; i < honeypotSelectors.length; i++) {
      var honeypot = formElement.querySelector(honeypotSelectors[i]);
      if (honeypot && honeypot.value && honeypot.value.trim() !== '') {
        return true; // Es un bot
      }
    }
    return false;
  }
  
  // Exponer API global
  window.DataCRMTracking = {
    getData: getTrackingData,
    checkHoneypot: checkHoneypot,
    getDataWithHoneypot: function(formElement) {
      var data = getTrackingData();
      if (formElement) {
        data.is_bot_honeypot = checkHoneypot(formElement);
      }
      return data;
    }
  };
  
  console.log('[DataCRM Tracking] Script cargado correctamente');
})();
`.trim();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Datos de Tracking</h1>
          <p className="text-slate-500 mt-1">
            Configuración de campos extra capturados en formularios
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-slate-100 rounded-lg">
            <span className="text-sm text-slate-600">Total campos: </span>
            <span className="font-bold text-slate-900">{getTotalFields()}</span>
          </div>
          <button
            onClick={() => setShowCode(!showCode)}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <Code className="w-4 h-4" />
            {showCode ? 'Ocultar Código' : 'Ver Código JS'}
          </button>
        </div>
      </div>

      {/* JavaScript Code */}
      {showCode && (
        <div className="bg-slate-900 rounded-xl p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Code className="w-5 h-5" />
              Código de Tracking para tu sitio web
            </h3>
            <button
              onClick={() => copyToClipboard(trackingCode)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm"
            >
              {copiedKey === trackingCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              Copiar
            </button>
          </div>
          <pre className="text-green-400 text-sm overflow-x-auto whitespace-pre-wrap">
            {trackingCode}
          </pre>
          <div className="mt-4 p-3 bg-blue-900/50 rounded-lg">
            <p className="text-blue-200 text-sm flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Agrega este código antes del cierre de &lt;/body&gt; en tu sitio. Luego, al enviar formularios, 
              incluye <code className="bg-blue-800 px-1 rounded">window.DataCRMTracking.getData()</code> en el payload.
            </p>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Database className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">¿Cómo funciona?</h3>
            <p className="text-slate-600 text-sm mt-1">
              Estos datos se capturan automáticamente cuando un visitante llena un formulario conectado a DataCRM. 
              La información se guarda en cada lead y permite analizar el comportamiento, fuentes de tráfico, 
              y calidad de los leads.
            </p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {Object.entries(trackingFields).map(([key, category]) => {
          const Icon = categoryIcons[key] || Database;
          const colors = categoryColors[key] || categoryColors.attribution;
          const isExpanded = expandedCategories[key];
          
          return (
            <div key={key} className={`bg-white rounded-xl border ${colors.border} overflow-hidden`}>
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(key)}
                className={`w-full flex items-center justify-between p-4 ${colors.bg} hover:opacity-90 transition-opacity`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-white`}>
                    <Icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <div className="text-left">
                    <h3 className={`font-semibold ${colors.text}`}>{category.category}</h3>
                    <p className="text-xs text-slate-500">{category.fields?.length || 0} campos</p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className={`w-5 h-5 ${colors.text}`} />
                ) : (
                  <ChevronRight className={`w-5 h-5 ${colors.text}`} />
                )}
              </button>
              
              {/* Fields */}
              {isExpanded && (
                <div className="divide-y divide-slate-100">
                  {category.fields?.map((field, idx) => (
                    <div key={idx} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-slate-900">{field.name}</h4>
                            <button
                              onClick={() => copyToClipboard(field.key)}
                              className="p-1 hover:bg-slate-100 rounded"
                              title="Copiar variable"
                            >
                              {copiedKey === field.key ? (
                                <Check className="w-3 h-3 text-green-500" />
                              ) : (
                                <Copy className="w-3 h-3 text-slate-400" />
                              )}
                            </button>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{field.description}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded font-mono">
                              {field.key}
                            </span>
                            <span className="text-xs text-slate-400">
                              Método: {field.method}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-slate-400">Ejemplo:</span>
                          <p className="text-sm text-slate-600 font-mono">{field.example}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminTracking;
